import type { ReactNode } from 'react';
import { ConsoleProvider, useConsole, useOperator } from './context';
import type { Console } from './context';
import { SignIn } from './SignIn';
import { Fleet } from './Fleet';
import { TenantDetail } from './TenantDetail';
import { Audit } from './Audit';
import { Jobs } from './Jobs';
import { Wizard } from './Wizard';
import { Rich } from './bits';
import { ToastProvider } from './kit';
import { useRoute } from './router';
import type { Route } from './router';
import { actorOf } from '../auth/fake';
import { useT } from '../messages';

/**
 * The shell: three places and an address bar.
 *
 * `apps/app` has six tabs because a counter has six jobs. This seat has three
 * — **Businesses**, **Log**, **Checks & imports** — the three nouns spec §6.3
 * lists, minus the ones that are properties of a business rather than screens
 * of their own. Keys, webhooks, deliveries and the lifecycle all live on the
 * business page, because every one of them is a question about *that business*,
 * and a "Keys" screen would have collected fourteen tenants' credentials under
 * one word.
 *
 * **Every screen has an address** (`router.ts`) — the redesign's first
 * decision (docs/console-redesign-plan.md §2). This comment used to argue the
 * opposite: no router, because a URL that survived a paste would be "a
 * cross-tenant screen in somebody's chat history". That does not hold. Every
 * read behind every screen is operator-authenticated and audited, so a pasted
 * link opens nothing its reader could not already open, and a tenant id is a
 * slug rather than a secret. What having no address cost was real: a refresh
 * threw the operator back to the fleet, Back left the console, and "look at
 * their Website tab" could not be said with a link. So the shell reads the
 * route, draws the screen it names, and writes the route when the operator
 * moves.
 *
 * The header states two things at all times: which mode the credential is in
 * (a fake token is never allowed to look like a real one), and the
 * `audit_log.actor` this session writes. The second is not decoration — every
 * request from this console, reads included, becomes a row under that name.
 * The product name in the corner is clearDay's (JSWaves is the company), and
 * like every name it is the same in every language.
 */

export function App({ console: value }: { console: Console }): ReactNode {
  return (
    <ConsoleProvider console={value}>
      <ToastProvider>
        <Authenticated />
      </ToastProvider>
    </ConsoleProvider>
  );
}

function Authenticated(): ReactNode {
  const { auth } = useConsole();
  const user = useOperator();
  if (user === null) return <SignIn auth={auth} />;
  return <Shell />;
}

type Place = 'fleet' | 'log' | 'checks';

/** Which top-bar place a route belongs to: a business and the wizard are the fleet's. */
function placeOf(route: Route): Place {
  if (route.kind === 'log') return 'log';
  if (route.kind === 'checks') return 'checks';
  return 'fleet';
}

function Shell(): ReactNode {
  const { auth } = useConsole();
  const t = useT();
  const user = useOperator();
  const [route, navigate] = useRoute();
  const place = placeOf(route);

  return (
    <div className="console">
      <header className="topbar">
        <div className="topbar__brand">
          {/* The product name is a name, in every language. */}
          clearDay <span className="topbar__seat">{t('app.seat')}</span>
        </div>

        <nav className="topbar__tabs" aria-label={t('app.nav.label')}>
          <PlaceButton
            current={place}
            id="fleet"
            label={t('app.tab.fleet')}
            onPick={() => navigate({ kind: 'fleet' })}
          />
          <PlaceButton
            current={place}
            id="log"
            label={t('app.tab.audit')}
            onPick={() => navigate({ kind: 'log' })}
          />
          <PlaceButton
            current={place}
            id="checks"
            label={t('app.tab.jobs')}
            onPick={() => navigate({ kind: 'checks' })}
          />
        </nav>

        <div className="topbar__who">
          {auth.kind === 'fake' ? (
            <span className="topbar__dev" title={t('app.devToken.title')}>
              {t('app.devToken')}
            </span>
          ) : null}
          <span className="topbar__actor">{user === null ? '' : actorOf(user)}</span>
          <button type="button" className="topbar__signout" onClick={() => void auth.signOut()}>
            {t('app.signOut')}
          </button>
        </div>
      </header>

      <main className="console__body">
        {route.kind === 'fleet' ? (
          <Fleet
            onOpen={(tenantId) => navigate({ kind: 'business', tenantId, tab: 'overview' })}
            onCreate={() => navigate({ kind: 'wizard' })}
          />
        ) : null}

        {route.kind === 'business' ? (
          <TenantDetail
            // A different business is a different page: nothing handed between
            // one business's cards may survive into another's.
            key={route.tenantId}
            tenantId={route.tenantId}
            tab={route.tab}
            editor={route.editor === true}
            answers={route.answers === true}
            onTab={(tab) => navigate({ kind: 'business', tenantId: route.tenantId, tab })}
            onSitePage={(page) =>
              navigate({
                kind: 'business',
                tenantId: route.tenantId,
                tab: 'site',
                ...(page === 'editor' ? { editor: true } : {}),
                ...(page === 'answers' ? { answers: true } : {}),
              })
            }
            item={route.item}
            onItem={(item) =>
              // Replace, not push: choosing the next thing in a list is not a
              // step Back should walk through twenty times.
              navigate(
                { kind: 'business', tenantId: route.tenantId, tab: 'catalog', item },
                { replace: true },
              )
            }
            onBack={() => navigate({ kind: 'fleet' })}
            onAudit={(tenantId) => navigate({ kind: 'log', tenantId })}
            onJobs={(tenantId) => navigate({ kind: 'checks', tenantId })}
          />
        ) : null}

        {route.kind === 'wizard' ? (
          <Wizard
            onDone={(tenantId) => navigate({ kind: 'business', tenantId, tab: 'overview' })}
            onCancel={() => navigate({ kind: 'fleet' })}
          />
        ) : null}

        {route.kind === 'log' ? (
          <Audit key={route.tenantId ?? ''} tenantId={route.tenantId} />
        ) : null}

        {route.kind === 'checks' ? (
          <Jobs key={route.tenantId ?? ''} tenantId={route.tenantId} />
        ) : null}
      </main>

      <footer className="console__foot">
        <Rich text={t('app.footer')} />
      </footer>
    </div>
  );
}

function PlaceButton({
  current,
  id,
  label,
  onPick,
}: {
  current: Place;
  id: Place;
  label: string;
  onPick: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      className={`topbar__tab${current === id ? ' is-current' : ''}`}
      aria-current={current === id ? 'page' : undefined}
      onClick={onPick}
    >
      {label}
    </button>
  );
}
